'use strict';

// Sandbox Guide: https://developers.dwolla.com/guides/sandbox

const { Client } = require('./dwolla-client/src');
const { getPropValue } = require('../utilities');

const log = true;

var _parent = {};
var _client = {};
var _apiUrl = `https://api-sandbox.dwolla.com`;

var dwolla = {
  init: p => {
    _parent = p;

    // Initialize Dwolla Client (ONLY IF envs are available - causes invariant issues in admin)
    if (p.config.dwolla_env && p.config.dwolla_key && p.config.dwolla_secret) {
      _client = new Client({
        environment: p.config.dwolla_env,
        key: p.config.dwolla_key,
        secret: p.config.dwolla_secret,
      });
    }

    // Set Dwolla API URL based on env (ONLY IF envs are available)
    if (p.config.dwolla_env) {
      _apiUrl = p.config.dwolla_env === `production` ? `https://api.dwolla.com` : `https://api-sandbox.dwolla.com`;
    }
  },

  /** Retrieve the root links from Dwolla */
  getRootLinks: async () => {
    try {
      let rootLinks = null;

      const res = await _client.get(`/`);
      if (getPropValue(res, `body._links`)) rootLinks = res.body._links;

      log && console.log(`Got dwolla rootLinks`);
      if (!rootLinks) throw new Error(`Dwolla failed to get root links!`);
      return rootLinks;
    } catch (err) {
      console.error(`Error in getRootLinks():`, err);
      throw err;
    }
  },

  /** Retrieve the root account ID from Dwolla */
  getRootAccountId: async () => {
    try {
      let rootAccountId = null;

      const res = await _client.get(`/`);
      if (getPropValue(res, `body._links.account.href`)) {
        rootAccountId = res.body._links.account.href;
        if (rootAccountId.includes(`.dwolla.com/accounts/`)) rootAccountId = rootAccountId.split(`accounts/`)[1];
      }

      log && console.log(`Got Dwolla rootAccountId`);
      if (!rootAccountId) throw new Error(`Dwolla failed to get root account ID!`);
      return rootAccountId;
    } catch (err) {
      console.error(`Error in getRootAccountId():`, err);
      throw err;
    }
  },

  /** Retrieve the root funding sources from Dwolla */
  getRootFundingSources: async () => {
    try {
      const rootAccountId = await _parent.dwolla.getRootAccountId();
      if (!rootAccountId) throw new Error(`Dwolla failed to get root funding sources, no rootAccountId found!`);

      const reqUrl = `${_apiUrl}/accounts/${rootAccountId}/funding-sources`;

      let rootFundingSources = null;

      const res = await _client.get(reqUrl);
      if (getPropValue(res, `body._embedded`)) rootFundingSources = res.body._embedded['funding-sources'];

      log && console.log(`Got Dwolla rootFundingSources`);
      if (!rootFundingSources) throw new Error(`Dwolla failed to get root funding sources!`);
      return rootFundingSources;
    } catch (err) {
      console.error(`Error in getRootFundingSources():`, err);
      throw err;
    }
  },

  /** Retrieve the master bank and balance funding source IDs from Dwolla */
  getRootFundingSourceIds: async () => {
    try {
      const rootFundingSources = await _parent.dwolla.getRootFundingSources();
      if (!rootFundingSources || rootFundingSources.length <= 0)
        throw new Error(`Dwolla failed to get root funding source IDs, no rootFundingSources found!`);

      let masterFundingSource = rootFundingSources.find(rfs => rfs.id === process.env.DWLA_FUND_SRC_ID);
      if (masterFundingSource && masterFundingSource.length > 0) masterFundingSource = masterFundingSource[0];

      let balanceFundingSource = rootFundingSources.find(rfs => rfs.name.toLowerCase().includes(`balance`));
      if (balanceFundingSource && balanceFundingSource.length > 0) balanceFundingSource = balanceFundingSource[0];

      const masterId = (masterFundingSource && masterFundingSource.id) || null;
      const balanceId = (balanceFundingSource && balanceFundingSource.id) || null;

      return { masterId, balanceId };
    } catch (err) {
      console.error(`Error in getRootFundingSourceIds():`, err);
      throw err;
    }
  },

  /** Retrieve the master bank funding source ID from Dwolla */
  getMasterFundingSourceId: async () => {
    try {
      const rootFundingSources = await _parent.dwolla.getRootFundingSources();
      if (!rootFundingSources || rootFundingSources.length <= 0)
        throw new Error(`Dwolla failed to get master funding source ID, no rootFundingSources found!`);

      let masterFundingSource = rootFundingSources.find(rfs => rfs.id === process.env.DWLA_FUND_SRC_ID);
      if (masterFundingSource && masterFundingSource.length > 0) masterFundingSource = masterFundingSource[0];

      log && console.log(`Got Dwolla masterFundingSourceId`);
      if (!masterFundingSource || !masterFundingSource.id)
        throw new Error(`Dwolla failed to get master funding source ID!`);
      return masterFundingSource.id;
    } catch (err) {
      console.error(`Error in getMasterFundingSourceId():`, err);
      throw err;
    }
  },

  /** Retrieve the balance funding source ID from Dwolla */
  getBalanceFundingSourceId: async () => {
    try {
      const rootFundingSources = await _parent.dwolla.getRootFundingSources();
      if (!rootFundingSources || rootFundingSources.length <= 0)
        throw new Error(`Dwolla failed to get balance funding source ID, no rootFundingSources found!`);

      let balanceFundingSource = rootFundingSources.find(rfs => rfs.name.toLowerCase().includes(`balance`));
      if (balanceFundingSource && balanceFundingSource.length > 0) balanceFundingSource = balanceFundingSource[0];

      log && console.log(`Got Dwolla balanceFundingSourceId`);
      if (!balanceFundingSource || !balanceFundingSource.id)
        throw new Error(`Dwolla failed to get balance funding source ID!`);
      return balanceFundingSource.id;
    } catch (err) {
      console.error(`Error in getBalanceFundingSourceId():`, err);
      throw err;
    }
  },

  /** Retrieve balance (with amount) from Dwolla */
  getBalanceAmount: async () => {
    try {
      const balanceId = await _parent.dwolla.getBalanceFundingSourceId();
      if (!balanceId) throw new Error(`Dwolla failed to get balance amount, no balanceId found!`);

      const balance = await _parent.dwolla.getFundingSource(balanceId);
      if (!balance) throw new Error(`Dwolla failed to get balance amount, no balance object returned!`);

      const balanceAmount = getPropValue(balance, `balance.value`);
      log && console.log(`Got Dwolla balanceAmount`);
      return Number(balanceAmount);
    } catch (err) {
      console.error(`Error in getBalanceAmount():`, err);
      throw err;
    }
  },

  /** Retrieve a single customer from Dwolla */
  getCustomer: async dwollaCustomerId => {
    try {
      const reqUrl = `${_apiUrl}/customers/${dwollaCustomerId}`;

      let customer = null;

      const res = await _client.get(reqUrl);
      if (res && res.body) customer = res.body;

      log && console.log(`Got Dwolla customer`);
      if (!customer) throw new Error(`Dwolla failed to get customer!`);
      return customer;
    } catch (err) {
      console.error(`Error in getCustomer():`, err);
      throw err;
    }
  },

  /** Retrieve a list of all customers from Dwolla */
  getAllCustomers: async () => {
    try {
      let customers = null;

      const res = await _client.get(`customers`);
      if (getPropValue(res, `body._embedded.customers`)) customers = res.body._embedded.customers;

      log && console.log(`Got Dwolla customers`);
      if (!customers) throw new Error(`Dwolla failed to get customers!`);
      return customers;
    } catch (err) {
      console.error(`Error in getAllCustomers():`, err);
      throw err;
    }
  },

  /** Retrieve a single funding source from Dwolla */
  getFundingSource: async dwollaFundingSourceId => {
    try {
      const reqUrl = `${_apiUrl}/funding-sources/${dwollaFundingSourceId}`;

      let fundingSource = null;

      const res = await _client.get(reqUrl);
      if (res && res.body) fundingSource = res.body;

      log && console.log(`Got Dwolla fundingSource`);
      if (!fundingSource) throw new Error(`Dwolla failed to get funding source!`);
      return fundingSource;
    } catch (err) {
      console.error(`Error in getFundingSource():`, err);
      throw err;
    }
  },

  /** Retrieve a customer's funding sources from Dwolla */
  getFundingSourcesByCustomerId: async dwollaCustomerId => {
    try {
      const reqUrl = `${_apiUrl}/customers/${dwollaCustomerId}/funding-sources`;

      let fundingSources = null;

      const res = await _client.get(reqUrl);
      if (getPropValue(res, `body._embedded`) && res.body._embedded['funding-sources'])
        fundingSources = res.body._embedded['funding-sources'];

      log && console.log(`Got Dwolla fundingSources`);
      if (!fundingSources) throw new Error(`Dwolla failed to get funding sources by customer!`);
      return fundingSources;
    } catch (err) {
      console.error(`Error in getFundingSourcesByCustomerId():`, err);
      throw err;
    }
  },

  /** Retrieve a tranfers's failure reason from Dwolla */
  getFailureReasonByTransfer: async transferId => {
    try {
      const reqUrl = `${_apiUrl}/transfers/${transferId}/failure`;

      let failureReason = null;

      const res = await _client.get(reqUrl);
      if (
        getPropValue(res, `body.code`) &&
        getPropValue(res, `body.description`) &&
        getPropValue(res, `body.explanation`)
      )
        failureReason = `${res.body.code} - ${res.body.description} (${res.body.explanation})`;

      log && console.log(`Dwolla failureReason:`, failureReason);
      if (!failureReason) throw new Error(`Dwolla failed to get failure reason by transfer!`);
      return failureReason;
    } catch (err) {
      console.error(`Error in getFailureReasonByTransfer():`, err);
      throw err;
    }
  },

  getMassTransferItem: async transferId => {
    try {
      const reqUrl = `${_apiUrl}/mass-payments-items/${transferId}`;

      const res = await _client.get(reqUrl);
      let response = null;
      if (res && res.body) response = res.body;
      if (!response) throw new Error(`Dwolla failed to get mass transfer item!`);

      log && console.log(`Got Dwolla mass transfer response:`, JSON.stringify(response, null, 2));

      return response;
    } catch (err) {
      console.error(`Error in getMassTransferItem():`, err);
      throw err;
    }
  },

  getMassTransferItems: async transferId => {
    try {
      const reqUrl = `${_apiUrl}/mass-payments/${transferId}/items`;

      const res = await _client.get(reqUrl);
      let response = null;
      if (res && res.body) response = res.body;
      if (!response) throw new Error(`Dwolla failed to get mass transfer items!`);

      log && console.log(`Got Dwolla mass transfer response:`, JSON.stringify(response, null, 2));

      const items = response._embedded.items ? response._embedded.items : [];
      if (!items || !items.length) throw new Error(`Dwolla failed to get mass transfer items!`);

      const getTransferId = item => {
        let transferId = null;
        if (item._links && item._links.transfer && item._links.transfer.href) {
          if (item._links.transfer.href.includes(`.dwolla.com/transfers/`))
            transferId = item._links.transfer.href.split(`transfers/`)[1];
        }
        return transferId;
      };

      const itemizedTransfers = items.map(item => ({
        id: item.correlationId,
        transferToken: getTransferId(item),
      }));

      return itemizedTransfers;
    } catch (err) {
      console.error(`Error in getMassTransferItems():`, err);
      throw err;
    }
  },

  getMassTransferCorrelationId: async transferId => {
    try {
      const reqUrl = `${_apiUrl}/mass-payments/${transferId}`;

      const res = await _client.get(reqUrl);
      let response = null;
      if (res && res.body) response = res.body;
      if (!response || !response.correlationId) throw new Error(`Dwolla failed to get mass transfer correlation ID!`);

      log && console.log(`Got Dwolla mass transfer response:`, JSON.stringify(response, null, 2));

      return response.correlationId ? Number(response.correlationId) : null;
    } catch (err) {
      console.error(`Error in getMassTransferCorrelationId():`, err);
      throw err;
    }
  },

  /** Create a Dwolla customer to send money to, return the dwolla ID */
  postReceiveOnlyCustomer: async (firstName, lastName, email) => {
    try {
      const req = {
        firstName,
        lastName,
        email,
        type: `receive-only`,
      };
      log && console.log(`Using request to post customer:`, email);

      let customerId = null;

      const res = await _client.post(`customers`, req);
      if (res && res.headers) {
        customerId = res.headers.get(`location`);
        if (customerId.includes(`.dwolla.com/customers/`)) customerId = customerId.split(`customers/`)[1];
      }

      log && console.log(`Dwolla customerId:`, customerId);
      if (!customerId) throw new Error(`Dwolla failed to post customer, no customerId was generated!`);
      return customerId;
    } catch (err) {
      console.error(`Error in postReceiveOnlyCustomer():`, err);
      throw err;
    }
  },

  /** Create a funding source for a dwolla customer */
  postFundingSource: async (dwollaCustomerId, routingNumber, accountNumber, bankAccountType, name) => {
    try {
      const reqUrl = `${_apiUrl}/customers/${dwollaCustomerId}/funding-sources`;
      const req = {
        routingNumber,
        accountNumber,
        bankAccountType,
        name,
      };
      //'name' here is the bank account name entered by the user
      log && console.log(`Using request to post funding source for ${name}`);

      let fundingSourceId = null;

      const res = await _client.post(reqUrl, req);
      if (res && res.headers) {
        fundingSourceId = res.headers.get(`location`);
        if (fundingSourceId.includes(`.dwolla.com/funding-sources/`))
          fundingSourceId = fundingSourceId.split(`funding-sources/`)[1];
      }

      log && console.log(`Got Dwolla fundingSourceId`);
      if (!fundingSourceId) throw new Error(`Dwolla failed to post funding source, no fundingSourceId was generated!`);
      return fundingSourceId;
    } catch (err) {
      console.error(`Error in postFundingSource():`, err);
      throw err;
    }
  },

  /** Initiate a transfer from our balance to the customer bank */
  postTransfer: async (dwollaFundingSourceId, amount, correlationId) => {
    try {
      const balanceFundingSourceId = await _parent.dwolla.getBalanceFundingSourceId();
      if (!balanceFundingSourceId) throw new Error(`Dwolla failed to post transfer, no balanceFundingSourceId found!`);

      const req = {
        _links: {
          source: {
            href: `${_apiUrl}/funding-sources/${balanceFundingSourceId}`,
          },
          destination: {
            href: `${_apiUrl}/funding-sources/${dwollaFundingSourceId}`,
          },
        },
        amount: {
          currency: `USD`,
          value: `${amount.toFixed(2)}`,
        },
        correlationId: correlationId,
      };

      log && console.log(`Using request to post transfer:`, JSON.stringify(req, null, 2));

      let transferId = null;

      const res = await _client.post('transfers', req);
      if (res && res.headers) {
        transferId = res.headers.get(`location`);
        if (transferId.includes(`.dwolla.com/transfers/`)) transferId = transferId.split(`transfers/`)[1];
      }

      log && console.log(`Got Dwolla transferId`);
      if (!transferId) throw new Error(`Dwolla failed to post transfer, no transferId was generated!`);
      return transferId;
    } catch (err) {
      console.error(`Error in postTransfer():`, err);
      throw err;
    }
  },

  postMassTransfer: async (driverPayArray = [], correlationId) => {
    try {
      if (!driverPayArray || !driverPayArray.length > 0) {
        throw new Error(`Failed to post Dwolla mass transfer, no driverPayArray provided!`);
      }

      const itemizedTransfers = driverPayArray
        .filter(driverPay => driverPay.success !== false)
        .map(driverPay => {
          return {
            _links: {
              destination: {
                href: `${_apiUrl}/funding-sources/${driverPay.bankAccountToken}`,
              },
            },
            amount: {
              currency: 'USD',
              value: driverPay.amount,
            },
            metadata: {
              ...driverPay,
            },
            correlationId: driverPay.payoutId,
          };
        });

      if (!itemizedTransfers || !itemizedTransfers.length > 0) {
        throw new Error(`Failed to post Dwolla mass transfer, no transfer items provided!`);
      }

      const balanceFundingSourceId = await _parent.dwolla.getBalanceFundingSourceId();
      if (!balanceFundingSourceId) {
        throw new Error(`Dwolla failed to post mass transfer, no balanceFundingSourceId found!`);
      }

      const req = {
        _links: {
          source: {
            href: `${_apiUrl}/funding-sources/${balanceFundingSourceId}`,
          },
        },
        items: itemizedTransfers,
        correlationId: correlationId,
      };

      log && console.log(`Using request to post mass transfer:`, JSON.stringify(req, null, 2));

      let transferId = null;
      const res = await _client.post('mass-payments', req);
      if (res && res.headers) {
        transferId = res.headers.get(`location`);
        // Extract just the ID part from the URL. Using regex to avoid issues with sandbox vs prod urls.
        const match = transferId.match(/\/mass-payments\/([^\/]+)$/);
        if (match) {
          transferId = match[1];
        }
      }

      if (!transferId) throw new Error(`Dwolla failed to post mass transfer, no transferId was generated!`);

      log && console.log(`Got Dwolla transferId for mass transfer:`, transferId);
      return transferId;
    } catch (err) {
      console.error(`Error in postMassTransfer():`, err);
      throw err;
    }
  },

  /** Initiate a transfer from our master bank to the dwolla balance */
  postTopUpTransfer: async amount => {
    try {
      const rootRes = await _parent.dwolla.getRootFundingSourceIds();
      if (!rootRes.masterId) throw new Error(`Dwolla failed to post top-up transfer, no masterId found!`);
      if (!rootRes.balanceId) throw new Error(`Dwolla failed to post top-up transfer, no balanceId found!`);

      const req = {
        _links: {
          source: {
            href: `${_apiUrl}/funding-sources/${rootRes.masterId}`,
          },
          destination: {
            href: `${_apiUrl}/funding-sources/${rootRes.balanceId}`,
          },
        },
        amount: {
          currency: `USD`,
          value: `${amount.toFixed(2)}`,
        },
      };
      log && console.log(`Using request to post top-up transfer...`);

      let transferId = null;

      const res = await _client.post('transfers', req);
      if (res && res.headers) {
        transferId = res.headers.get(`location`);
        if (transferId.includes(`.dwolla.com/transfers/`)) transferId = transferId.split(`transfers/`)[1];
      }

      log && console.log(`Got Dwolla transferId`);
      if (!transferId) throw new Error(`Dwolla failed to post top-up transfer, no transferId was generated!`);
      return transferId;
    } catch (err) {
      console.error(`Error in postTopUpTransfer():`, err);
      throw err;
    }
  },

  /** Delete a funding source from our Dwolla system */
  deleteFundingSource: async dwollaFundingSourceId => {
    try {
      const reqUrl = `${_apiUrl}/funding-sources/${dwollaFundingSourceId}`;
      const req = {
        removed: true,
      };

      let wasRemoved = false;

      const res = await _client.post(reqUrl, req);
      if (res && res.body && res.body.removed) wasRemoved = true;

      if (!wasRemoved) throw new Error(`Dwolla failed to delete funding source!`);

      log && console.log(`Dwolla successfully removed funding source!`);
      return wasRemoved;
    } catch (err) {
      console.error(`Error in deleteFundingSource():`, err);
      throw err;
    }
  },

  /** Cancel a transfer in the Dwolla system */
  cancelTransfer: async dwollaTransactionId => {
    try {
      const reqUrl = `${_apiUrl}/transfers/${dwollaTransactionId}`;
      const req = {
        status: `cancelled`,
      };

      let wasCanceled = false;

      const res = await _client.post(reqUrl, req);
      if (res && res.body && res.body.status === `cancelled`) wasCanceled = true;

      if (!wasCanceled) throw new Error(`Dwolla failed to cancel transfer!`);
      return wasCanceled;
    } catch (err) {
      console.error(`Error in cancelTransfer():`, err);
      throw err;
    }
  },

  /** List all dwolla webhook subscriptions */
  webhooksListAllSubscriptions: async () => {
    try {
      let webhooks = null;

      const res = await _client.get('webhook-subscriptions');
      if (getPropValue(res, `body._embedded`) && res.body._embedded['webhook-subscriptions'])
        webhooks = res.body._embedded['webhook-subscriptions'];

      log && console.log(`Got Dwolla webhooks`);
      if (!webhooks) throw new Error(`Dwolla failed to get webhook subscriptions!`);
      return webhooks;
    } catch (err) {
      console.error(`Error in webhooksListAllSubscriptions():`, err);
      throw err;
    }
  },

  /** Create a dwolla webhook subscription */
  webhooksCreateSubscription: async url => {
    try {
      const req = {
        url: url,
        secret: _parent.config.dwolla_secret,
      };

      let webhookId = null;

      const res = await _client.post('webhook-subscriptions', req);
      if (res && res.headers) {
        webhookId = res.headers.get(`location`);
        if (webhookId.includes(`.dwolla.com/webhook-subscriptions/`))
          webhookId = webhookId.split(`webhook-subscriptions/`)[1];
      }

      log && console.log(`Posting webhook subscription...`);
      if (!webhookId) throw new Error(`Dwolla failed to post webhook subscription!`);
      return webhookId;
    } catch (err) {
      console.error(`Error in webhooksCreateSubscription():`, err);
      throw err;
    }
  },

  /** Delete a dwolla webhook subscription */
  webhooksDeleteSubscription: async dwollaWebhookId => {
    try {
      const reqUrl = `${_apiUrl}/webhook-subscriptions/${dwollaWebhookId}`;

      let wasDeleted = false;

      const res = await _client.delete(reqUrl);
      if (res.status === 200) wasDeleted = true;

      if (!wasDeleted) throw new Error(`Dwolla failed to delete webhook subscription!`);

      log && console.log(`Dwolla successfully removed webhook!`);
      return wasDeleted;
    } catch (err) {
      console.error(`Error in webhooksDeleteSubscription():`, err);
      throw err;
    }
  },
};

module.exports = dwolla;
